import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { formatCurrency } from '../../utilities/helpers'
import { getAccountingReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { ReportActions } from '../ReportActions'
import AccountingMonthChart from './AccountingMonthChart'
import { exportAccountingReportExcel } from './accountingReportExcel'
import {
  ALL_MONTHS,
  getActiveMonths,
  getTeamRows,
  getViewTotals,
  monthLabel,
  percentOfTotal,
} from './accountingReportView'

import './accountingReportWrapper.scss'

const yearStart = () => moment().startOf('year').toDate()
const todayEnd = () => moment().endOf('day').toDate()

export default function AccountingReportWrapper({ organization }) {
  const [fromDate, setFromDate] = useState(yearStart)
  const [toDate, setToDate] = useState(todayEnd)
  const [selectedMonth, setSelectedMonth] = useState(ALL_MONTHS)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [report, setReport] = useState(null)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false

    const loadReport = async () => {
      if (!organization?.uuid || !fromDate || !toDate) return

      setIsLoadingData(true)
      setLoadError('')

      try {
        const res = await getAccountingReport(
          organization.uuid,
          moment(fromDate).format('YYYY-MM-DD'),
          moment(toDate).format('YYYY-MM-DD')
        )
        if (!cancelled) {
          setReport(res?.data || null)
          setSelectedMonth(ALL_MONTHS)
        }
      } catch (error) {
        if (cancelled) return
        console.error('Error loading Accounting report:', error)
        setLoadError('Unable to load Accounting report.')
        setReport(null)
      } finally {
        if (!cancelled) {
          setIsLoadingData(false)
          completeRefresh()
        }
      }
    }

    loadReport()
    return () => {
      cancelled = true
    }
  }, [organization?.uuid, fromDate, toDate, refreshToken])

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || []
    setFromDate(start || null)
    setToDate(end ? moment(end).endOf('day').toDate() : null)
  }

  const handleExcelExport = () => {
    exportAccountingReportExcel({
      report,
      organizationName: organization?.name,
      fromDate,
      toDate,
    })?.catch((err) => alert(err.message))
  }

  return (
    <section className="spacer-sm" id="accounting-report-wrapper">
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Service Fee Ledger</h1>
        </div>
        <p className="section-header-desc">
          Card fees are recorded as collected. Non-card fees are recorded as
          owed until settled. Complimentary orders are not charged a service
          fee.
        </p>
      </header>

      <ReportActions
        isLoading={isLoadingData}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
        selected="date-range"
        fromDate={fromDate}
        toDate={toDate}
        onDateRangeChange={handleDateRangeChange}
        data={report?.teams}
        downloadReport={handleExcelExport}
      />

      <Results
        isLoading={isLoadingData}
        report={report}
        loadError={loadError}
        selectedMonth={selectedMonth}
        onSelectMonth={setSelectedMonth}
      />
    </section>
  )
}

function Results({
  isLoading,
  report,
  loadError,
  selectedMonth,
  onSelectMonth,
}) {
  const totals = useMemo(
    () => getViewTotals(report, selectedMonth),
    [report, selectedMonth]
  )
  const teamRows = useMemo(
    () => getTeamRows(report, selectedMonth),
    [report, selectedMonth]
  )

  if (isLoading) return <LoadingContainer />

  if (loadError) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>{loadError}</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (!report?.teams?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No service fees found for the selected date range</p>
        </EmptyContainer>
      </Card>
    )
  }

  const periodLabel =
    selectedMonth === ALL_MONTHS ? 'all months' : monthLabel(selectedMonth)
  const collectedShare = percentOfTotal(totals.collected, totals.total)
  const owedShare = percentOfTotal(totals.owed, totals.total)

  return (
    <>
      <div className="accounting-report-pills" role="tablist">
        <Button
          type="button"
          variant="default"
          className={`accounting-report-pill ${
            selectedMonth === ALL_MONTHS ? 'is-active' : ''
          }`}
          onClick={() => onSelectMonth(ALL_MONTHS)}
        >
          All months
        </Button>
        {getActiveMonths(report).map((monthKey) => (
          <Button
            key={monthKey}
            type="button"
            variant="default"
            className={`accounting-report-pill ${
              selectedMonth === monthKey ? 'is-active' : ''
            }`}
            onClick={() => onSelectMonth(monthKey)}
          >
            {monthLabel(monthKey)}
          </Button>
        ))}
      </div>

      <div className="accounting-report-kpis">
        <Kpi
          title={`Total service fees — ${periodLabel}`}
          value={formatCurrency(totals.total)}
          subtitle={`${totals.teamCount} ${
            totals.teamCount === 1 ? 'team' : 'teams'
          }`}
        />
        <Kpi
          title="Collected — card"
          value={formatCurrency(totals.collected)}
          subtitle={`${collectedShare}% of total`}
          tone="collected"
        />
        <Kpi
          title="Owed — non-card"
          value={formatCurrency(totals.owed)}
          subtitle={`${owedShare}% of total`}
          tone="owed"
        />
      </div>

      <section className="accounting-report-panel">
        <h2>Month over month</h2>
        <AccountingMonthChart report={report} />
      </section>

      <section className="accounting-report-panel">
        <h2>By team</h2>
        <div className="accounting-report-table-wrap">
          <Table className="align-middle accounting-report-table">
            <thead>
              <tr>
                <th>Team</th>
                <th className="text-end">Collected (card)</th>
                <th className="text-end">Owed (non-card)</th>
                <th className="text-end">Total fees</th>
                <th>% collected</th>
              </tr>
            </thead>
            <tbody>
              {teamRows.map((row) => (
                <tr key={row.organizationUUID}>
                  <td>{row.organizationName}</td>
                  <td className="text-end is-collected">
                    {formatCurrency(row.collected)}
                  </td>
                  <td className="text-end is-owed">
                    {formatCurrency(row.owed)}
                  </td>
                  <td className="text-end">{formatCurrency(row.total)}</td>
                  <td>
                    <div className="accounting-report-share">
                      <span
                        className="accounting-report-share-bar"
                        style={{
                          width: `${Math.max(row.percentCollected, 0)}%`,
                        }}
                      />
                      <strong>{Math.round(row.percentCollected)}%</strong>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>All teams</td>
                <td className="text-end is-collected">
                  {formatCurrency(totals.collected)}
                </td>
                <td className="text-end is-owed">
                  {formatCurrency(totals.owed)}
                </td>
                <td className="text-end">{formatCurrency(totals.total)}</td>
                <td>
                  <strong>{collectedShare}%</strong>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </section>
    </>
  )
}

function Kpi({ title, value, subtitle, tone }) {
  return (
    <div className={`accounting-report-kpi ${tone ? `is-${tone}` : ''}`}>
      <p className="accounting-report-kpi-title">{title}</p>
      <p className="accounting-report-kpi-value">{value}</p>
      <p className="accounting-report-kpi-sub">{subtitle}</p>
    </div>
  )
}
